import React, { useEffect, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  CircularProgress,
} from "@mui/material";

const API_BASE = process.env.REACT_APP_API_URL || "http://localhost:3001";

const DEFAULT_FORCE_INVITE = {
  title: "You're Invited to Join the Game!",
  message:
    "The host has picked something special just for you. Get ready — the fun is about to begin!",
  timerSeconds: 15,
};

const ForceInviteConfigSection = ({ compact = false }) => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [title, setTitle] = useState(DEFAULT_FORCE_INVITE.title);
  const [message, setMessage] = useState(DEFAULT_FORCE_INVITE.message);
  const [timerSeconds, setTimerSeconds] = useState(DEFAULT_FORCE_INVITE.timerSeconds);

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/settings/public/mym-titles-config`);
        const data = await res.json();
        const fi = data.forceInvite || {};
        setTitle(fi.title ?? DEFAULT_FORCE_INVITE.title);
        setMessage(fi.message ?? DEFAULT_FORCE_INVITE.message);
        setTimerSeconds(
          typeof fi.timerSeconds === "number"
            ? fi.timerSeconds
            : DEFAULT_FORCE_INVITE.timerSeconds
        );
      } catch (err) {
        console.error("Failed to fetch force invite config:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchConfig();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_BASE}/api/settings/mym-titles-config`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("admin_token")}`,
        },
        body: JSON.stringify({
          forceInvite: {
            title: title.trim(),
            message: message.trim(),
            timerSeconds: Math.max(1, Math.min(120, Number(timerSeconds) || 15)),
          },
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        console.error(data.message || "Failed to save force invite config");
      }
    } catch (err) {
      console.error("Failed to save force invite config:", err);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
        <CircularProgress size={28} sx={{ color: "#1DB954" }} />
      </Box>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        p: compact ? 1.5 : 2,
        mt: compact ? 2 : 3,
        backgroundColor: "#1E1E1E",
        border: "1px solid #333",
        borderRadius: "12px",
      }}
    >
      <Typography
        sx={{
          color: "#fff",
          fontWeight: 600,
          mb: 0.5,
          fontSize: compact ? "0.95rem" : "1.1rem",
        }}
      >
        Force Invite Splash
      </Typography>
      <Typography variant="body2" sx={{ color: "#888", mb: 2 }}>
        Shown to users when a game is forced open. Displays for the configured
        duration, then automatically opens the game.
      </Typography>

      <TextField
        fullWidth
        label="Invite title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ sx: { color: "#fff" } }}
        InputLabelProps={{ sx: { color: "#aaa" } }}
      />
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Invite message"
        value={message}
        onChange={(e) => setMessage(e.target.value)}
        sx={{ mb: 2 }}
        InputProps={{ sx: { color: "#fff" } }}
        InputLabelProps={{ sx: { color: "#aaa" } }}
      />
      <TextField
        label="Display duration (seconds)"
        type="number"
        value={timerSeconds}
        onChange={(e) => setTimerSeconds(e.target.value)}
        inputProps={{ min: 1, max: 120 }}
        helperText="1–120 seconds"
        sx={{ mb: 2, width: 220 }}
        InputProps={{ sx: { color: "#fff" } }}
        InputLabelProps={{ sx: { color: "#aaa" } }}
        FormHelperTextProps={{ sx: { color: "#666" } }}
      />
      <Button
        variant="contained"
        onClick={handleSave}
        disabled={saving}
        sx={{
          background: "linear-gradient(135deg, #ff00ff 0%, #00ffff 100%)",
          color: "#fff",
          textTransform: "none",
          fontWeight: 600,
        }}
      >
        {saving ? "Saving…" : "Save invite splash"}
      </Button>
    </Paper>
  );
};

export default ForceInviteConfigSection;
